import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

const ANCHOR_GAP = 16;
const FINAL_CORRECTION_DELAY = 900;
const LAYOUT_QUIET_TIME = 180;
const MAX_LAYOUT_WAIT = 2500;
const POSITION_TOLERANCE = 3;

const getHeaderOffset = () => {
  const header = document.querySelector("header");
  return header ? Math.ceil(header.getBoundingClientRect().height) : 0;
};

const getTargetTop = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return null;

  const headerOffset = getHeaderOffset() + ANCHOR_GAP;
  return Math.max(
    el.getBoundingClientRect().top + window.scrollY - headerOffset,
    0
  );
};

export default function ScrollToHash() {
  const { hash, pathname } = useLocation();

  useLayoutEffect(() => {
    if (!hash) return;

    const id = decodeURIComponent(hash.replace("#", ""));
    let rafId: number | null = null;
    let startTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let correctionTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    let stopped = false;
    const startedAt = performance.now();
    let lastChangeAt = startedAt;
    let lastTop: number | null = null;
    let pendingBlockingImages = 0;

    const markLayoutChanged = () => {
      lastChangeAt = performance.now();
    };

    const finalCorrection = () => {
      if (stopped) return;

      const targetTop = getTargetTop(id);
      if (targetTop === null) return;

      if (Math.abs(window.scrollY - targetTop) > POSITION_TOLERANCE) {
        window.scrollTo(0, targetTop);
      }
    };

    const smoothScrollToTarget = () => {
      const targetTop = getTargetTop(id);
      if (targetTop === null) return false;

      window.scrollTo({
        top: targetTop,
        behavior: "smooth",
      });

      correctionTimeoutId = setTimeout(
        finalCorrection,
        FINAL_CORRECTION_DELAY
      );
      return true;
    };

    const waitForQuietLayout = () => {
      rafId = null;
      if (stopped) return;

      const now = performance.now();
      const targetTop = getTargetTop(id);

      if (targetTop !== null) {
        if (
          lastTop === null ||
          Math.abs(targetTop - lastTop) > POSITION_TOLERANCE
        ) {
          lastTop = targetTop;
          markLayoutChanged();
        }

        const layoutIsQuiet =
          now - lastChangeAt >= LAYOUT_QUIET_TIME &&
          pendingBlockingImages === 0;
        const waitedLongEnough = now - startedAt >= MAX_LAYOUT_WAIT;

        if (layoutIsQuiet || waitedLongEnough) {
          smoothScrollToTarget();
          return;
        }
      }

      if (now - startedAt < MAX_LAYOUT_WAIT) {
        rafId = window.requestAnimationFrame(waitForQuietLayout);
      }
    };

    startTimeoutId = setTimeout(() => {
      if (window.scrollY === 0) window.scrollTo(0, 1);
      waitForQuietLayout();
    }, 0);

    if ("ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(markLayoutChanged);
      resizeObserver.observe(document.body);
    }

    mutationObserver = new MutationObserver(markLayoutChanged);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "src"],
    });

    window.addEventListener("load", markLayoutChanged);
    window.addEventListener("resize", markLayoutChanged);

    document.fonts?.ready.then(markLayoutChanged).catch(() => undefined);

    const isAboveTarget = (image: HTMLImageElement) => {
      const target = document.getElementById(id);
      if (!target) return true;
      return (
        image.getBoundingClientRect().top < target.getBoundingClientRect().top
      );
    };

    const pendingImages = Array.from(document.images).filter(
      (image) => !image.complete && isAboveTarget(image)
    );
    pendingBlockingImages = pendingImages.length;
    const handleImageSettled = () => {
      pendingBlockingImages = Math.max(pendingBlockingImages - 1, 0);
      markLayoutChanged();
    };

    pendingImages.forEach((image) => {
      image.addEventListener("load", handleImageSettled, { once: true });
      image.addEventListener("error", handleImageSettled, { once: true });
    });

    return () => {
      stopped = true;
      if (startTimeoutId) clearTimeout(startTimeoutId);
      if (correctionTimeoutId) clearTimeout(correctionTimeoutId);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener("load", markLayoutChanged);
      window.removeEventListener("resize", markLayoutChanged);
      pendingImages.forEach((image) => {
        image.removeEventListener("load", handleImageSettled);
        image.removeEventListener("error", handleImageSettled);
      });
    };
  }, [hash, pathname]);

  return null;
}
